import React, { Suspense, lazy, useEffect, useState } from "react";
import "./App.css";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { useBackButton } from "./hooks/useBackButton";
import PartyListPagination from "./components/party/PartyList";
import Header from "./components/Header";
import RequireAuth from "./components/RequireAuth";
import { Login } from "./components/Login";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ToastProvider from "./components/common/ToastProvider";
import GameListPagination from "./components/game/GameList";
import HavalaAccount from "./components/report/Havala Account/HavalaAccount";
import TrialBalance from "./components/report/TrialBalance";
import Havala from "./components/report/HavalaReport";
import SelectMatch from "./components/report/SelectMatch";
import Users from "./components/Utility/Users/Users";
import Tenants from "./components/Utility/Tenant/Tenants";
import TenantActivities from "./components/Utility/TenantActivities";
import QuickAddParty from "./components/Utility/QuickAddParty";
import InterestReport from "./components/Utility/InterestReport";
import CreateNotification from "./components/Utility/CreateNotification";
import SodaFormSequence from "./components/Utility/SodaFormSequence";
import ImportAccount from "./components/Utility/ImportAccount";
import QuickAddPartyDialog from "./components/party/QuickAddPartyDialog";
import Home from "./components/Home";
import CreateHavalaGlobalDialog from "./components/CreateHavalaGlobalDialog";
import PartyListReferenceDialog from "./components/dialogs/PartyListReferenceDialog";
import ProtectedRoute from "./routes/ProtectedRoute";
import { useAuthorization } from "./auth/useAuthorization";
import { Role } from "./auth/roles";
import { Navigate } from "react-router-dom";
import {
  PARTY_LIST,
  REPORT_LEDGER,
  REPORT_HAVALA,
  REPORT_TRIAL_BALANCE,
  REPORT_MATCH,
  USER_LIST,
  PARTY_CREATE,
  GAME_LIST,
  GAME_OPEN,
  REPORT_VIEW,
  MATCH_RECALL,
  IMPORT_ACCOUNT,
  QUICK_ADD_PARTY,
} from "./auth/permissions";

const GameBoardRouter = lazy(() => import("./components/game/GameBoardRouter"));
const LedgerAccount = lazy(() => import("./components/report/LedgerAccount"));
// const HawalaAccount = lazy(() => import('./components/report/HawalaAccount'));
// const TrialBalance = lazy(() => import('./components/report/TrialBalance'));

/**
 * PublicRoute - Redirects authenticated users away from public pages (like login)
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthorization();
  
  // If user is already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  // If not authenticated, show the public page (login)
  return <>{children}</>;
};

/** Keep the signed-in app off screen until auth is known. Signed-out visits go to login. */
const AuthEntry: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authenticated } = useAuth();
  const location = useLocation();

  if (authenticated === null) {
    return null;
  }

  if (!authenticated) {
    if (location.pathname !== "/login") {
      return <Navigate to="/login" replace />;
    }
    return <Login />;
  }

  return <>{children}</>;
};

function App() {
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCKS === 'true') {
    require("./utils/mocker/mock.ts");
  }
  const navigate = useNavigate();
  useBackButton();
  const [isQuickAddPartyOpen, setIsQuickAddPartyOpen] = useState(false);
  const [isPartyListReferenceOpen, setIsPartyListReferenceOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        // Trigger save logic here
      }

      if (event.ctrlKey && event.key === "n") {
        event.preventDefault();
        // Trigger new item logic here
      }

      if (event.altKey && ["p", "P"].includes(event.key)) {
        event.preventDefault();
        navigate("/party");
        // Trigger focus logic
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate]);

  // Listen for F4 event to open QuickAddPartyDialog
  useEffect(() => {
    const handleOpenQuickAddParty = () => {
      setIsQuickAddPartyOpen(true);
    };

    window.addEventListener('open-quick-add-party', handleOpenQuickAddParty as EventListener);

    return () => {
      window.removeEventListener('open-quick-add-party', handleOpenQuickAddParty as EventListener);
    };
  }, []);


  // Listen for F3 event to open Party List Reference dialog globally
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger if typing in input fields
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                             activeElement?.tagName === 'TEXTAREA' ||
                             activeElement?.getAttribute('contenteditable') === 'true';
      
      if (isInputFocused) {
        return;
      }

      // F3 (without ALT, CTRL, or SHIFT) - check both key and code
      if ((event.key === 'F3' || event.code === 'F3') && !event.altKey && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        setIsPartyListReferenceOpen(true);
      }
    };

    // Also listen for custom event from Header
    const handleOpenPartyListReference = () => {
      setIsPartyListReferenceOpen(true);
    };

    // Use capture phase to ensure we catch it before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('open-party-list-reference', handleOpenPartyListReference as EventListener);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('open-party-list-reference', handleOpenPartyListReference as EventListener);
    };
  }, []);

  const location = useLocation();
  const isGameBoardRoute = location.pathname.includes('/game/transactions/');

  return (
    <>
      <AuthProvider>
        <AuthEntry>
        <RequireAuth>
          {/* Always show Header, including on Game Board routes */}
          <Header />
        </RequireAuth>
        <Routes>
          {/* Protected routes (login required) */}
          <Route
            path="/party"
            element={
              <ProtectedRoute permission={PARTY_LIST} redirectTo="/game/oneday#home">
                <PartyListPagination />
              </ProtectedRoute>
            }
          />

          {/* Report Routes start*/}
          <Route
            path="/report/ledger"
            element={
              <ProtectedRoute permission={REPORT_LEDGER} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Ledger...</div>}>
                  <LedgerAccount />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/report/havala"
            element={
              <ProtectedRoute permission={REPORT_HAVALA} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Havala Account...</div>}>
                  <HavalaAccount />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/report/trial"
            element={
              <ProtectedRoute permission={REPORT_TRIAL_BALANCE} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Trial Balance...</div>}>
                  <TrialBalance />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/report/havala-report"
            element={
              <ProtectedRoute permission={REPORT_HAVALA} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Havala Report...</div>}>
                  <Havala />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/report/select-match"
            element={
              <ProtectedRoute permission={REPORT_MATCH} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Select Match....</div>}>
                  <SelectMatch />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Report Routes end*/}

          {/* Utility Routes start*/}
          <Route
            path="/utility/users"
            element={
              <ProtectedRoute permission={USER_LIST} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Users....</div>}>
                  <Users />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/utility/tenant"
            element={
              <ProtectedRoute role={Role.ADMIN} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Tenants....</div>}>
                  <Tenants />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/utility/tenant-activities"
            element={
              <ProtectedRoute role={Role.ADMIN} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Tenant Activities....</div>}>
                  <TenantActivities />
                </Suspense>
              </ProtectedRoute>
            }
          />

           <Route
            path="/utility/add-party"
            element={
              <ProtectedRoute allPermissions={[QUICK_ADD_PARTY, PARTY_CREATE]} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Quick Add Party....</div>}>
                  <QuickAddParty />
                </Suspense>
              </ProtectedRoute>
            }
          />

            <Route
            path="/utility/match-recall"
            element={
              <ProtectedRoute permission={MATCH_RECALL} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading MATCH RECALL....</div>}>
                  <SelectMatch />
                </Suspense>
              </ProtectedRoute>
            }
          />

            <Route
            path="/utility/interest-calculation"
            element={
              <ProtectedRoute permission={REPORT_VIEW} redirectTo="/game/oneday#home">
                <Suspense fallback={<div>Loading Interest Calculation....</div>}>
                  <InterestReport />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/utility/create-notification"
            element={
              <ProtectedRoute role={Role.ADMIN} redirectTo="/game/oneday#home">
                <CreateNotification />
              </ProtectedRoute>
            }
          />
          <Route
            path="/utility/import-account"
            element={
              <ProtectedRoute permission={IMPORT_ACCOUNT} redirectTo="/game/oneday#home">
                <ImportAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="/utility/soda-form-sequence"
            element={
              <ProtectedRoute role={Role.OWNER} redirectTo="/game/oneday#home">
                <SodaFormSequence />
              </ProtectedRoute>
            }
          />
          {/* Utility Routes end*/}

          <Route
            path="/game/transactions/:gameId"
            element={
              <ProtectedRoute permission={GAME_OPEN} redirectTo="/home">
                <Suspense fallback={<div></div>}>
                  <GameBoardRouter />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/game/oneday"
            element={
              <ProtectedRoute permission={GAME_LIST} redirectTo="/home">
                <Suspense fallback={<div></div>}>
                  <GameListPagination gameType="oneday" />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/game/session"
            element={
              <ProtectedRoute permission={GAME_LIST} redirectTo="/home">
                <Suspense fallback={<div></div>}>
                  <GameListPagination gameType="session" />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/game/test"
            element={
              <ProtectedRoute permission={GAME_LIST} redirectTo="/home">
                <Suspense fallback={<div></div>}>
                  <GameListPagination gameType="test" />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Home page */}
          <Route
            path="/home"
            element={<Home />}
          />

          {/* Public route - redirect to home if already logged in */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          {/* Root route - redirect to home */}
          <Route
            path="/"
            element={<Navigate to="/home" replace />}
          />
          
          {/* Catch-all route - redirect unauthorized access */}
          <Route
            path="*"
            element={
              <ProtectedRoute redirectTo="/game/oneday#home">
                <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
                  <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
                    <div className="text-6xl font-bold text-red-600 dark:text-red-400 mb-4">404</div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Page Not Found
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      The page you're looking for doesn't exist or you don't have permission to access it.
                    </p>
                    <button
                      onClick={() => navigate('/game/oneday#home')}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Go Home
                    </button>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
        </Routes>
        <CreateHavalaGlobalDialog />
        </AuthEntry>
      </AuthProvider>
      <ToastProvider />
      <QuickAddPartyDialog 
        isOpen={isQuickAddPartyOpen} 
        onClose={() => setIsQuickAddPartyOpen(false)} 
      />
      <PartyListReferenceDialog
        isOpen={isPartyListReferenceOpen}
        onClose={() => setIsPartyListReferenceOpen(false)}
      />
    </>
  );
}

export default App;
